import { useState, useEffect, useMemo, Suspense } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Layout,
  Menu,
  Button,
  Avatar,
  Dropdown,
  Space,
  Badge,
  ConfigProvider,
  Message,
  Input,
  Tooltip,
  Spin
} from '@arco-design/web-react';
import { useTranslation } from 'react-i18next';
import { getArcoLocale } from '../locales/arcoLocale';
import LanguageSwitcher from '@/modules/common/components/LanguageSwitcher';
import NotificationBell from '@/modules/notifications/components/NotificationBell';
import TaskBell from '@/modules/system/procedimientos/components/TaskBell';
import {
  IconCalendar,
  IconSun,
  IconMoon,
  IconSettings,
  IconPoweroff,
  IconUser,
  IconHome,
  IconBook,
  IconUserGroup,
  IconDesktop,
  IconFolder,
  IconApps,
  IconTool,
  IconMenuUnfold,
  IconThunderbolt,
  IconExperiment,
  IconDashboard,
  IconFile,
  IconExpand,
  IconShrink,
  IconLaunch,
  IconTag,
  IconCompass,
  IconMessage,
  IconSend,
  IconPen,
  IconLock,
  IconRobot,
  IconIdcard,
  IconNotification,
  IconMindMapping,
} from '@arco-design/web-react/icon';
import { useAuth } from '../modules/auth/useAuth.jsx';
import logo from '@/modules/common/assets/logo.png';
// El CSS de Arco ya se importa en index.js
import './AppLayout.css';

const { Sider, Header, Footer, Content } = Layout;
const { SubMenu } = Menu;

const AppLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, hasPermission, hasRole, isImpersonating, impersonatedUser, stopImpersonation } = useAuth();
  const { t, i18n } = useTranslation('navigation');
  const { t: tSys } = useTranslation('system');

  // Estados
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');
  const [collapsed, setCollapsed] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [openKeys, setOpenKeys] = useState([]);
  const [expandAll, setExpandAll] = useState(false);
  const [menuKey, setMenuKey] = useState(0); // Key para forzar re-render del menú
  const { data: appVersion = '' } = useQuery({
    queryKey: ['app', 'version'],
    queryFn: async () => {
      const res = await fetch('/version.json');
      const data = await res.json();
      return data.version || '';
    },
    staleTime: 30 * 60 * 1000, // 30 minutes - static file, rarely changes
    retry: 1,
  });
  const [menuSearchText, setMenuSearchText] = useState('');
  
  // Estructura del menú
  const menuItems = [
    // Home
    {
      key: 'home',
      icon: <IconHome />,
      title: t('modules.home'),
      path: '/',
      single: true,
    },

    // Portal de Alumno (2 niveles + tabs)
    {
      key: 'alumno',
      icon: <IconUser />,
      title: t('modules.student'),
      permission: 'student.portal.view',
      children: [
        { key: 'dashboard-alumno', icon: <IconDashboard />, title: t('student.dashboard'), path: '/alumno/dashboard', single: true },
        { key: 'mi-perfil-alumno', icon: <IconIdcard />, title: t('student.profile'), path: '/alumno/perfil', single: true },
        { key: 'mi-aula-alumno', icon: <IconDesktop />, title: t('student.myClassroom'), path: '/alumno/mi-aula', pathPrefix: '/alumno/mi-aula' },
        { key: 'recursos-eval-alumno', icon: <IconDesktop />, title: t('student.resourcesEvaluation'), path: '/alumno/recursos-eval', pathPrefix: '/alumno/recursos-eval' },
        { key: 'mi-expediente-alumno', icon: <IconFolder />, title: t('student.transcript'), path: '/alumno/expediente', pathPrefix: '/alumno/expediente' },
        { key: 'tramites-alumno', icon: <IconFile />, title: t('student.procedures'), path: '/alumno/tramites', pathPrefix: '/alumno/tramites' },
        { key: 'finanzas-alumno', icon: <IconApps />, title: t('student.finances'), path: '/alumno/finanzas', pathPrefix: '/alumno/finanzas' },
        { key: 'vida-estudiantil-alumno', icon: <IconCompass />, title: t('student.studentLife'), path: '/alumno/vida-estudiantil', pathPrefix: '/alumno/vida-estudiantil' }
      ]
    },

    // Portal de Docente (2 niveles + tabs)
    {
      key: 'docente',
      icon: <IconBook />,
      title: t('modules.teacher'),
      permission: 'teacher.portal.view',
      children: [
        { key: 'dashboard-docente', icon: <IconDashboard />, title: t('teacher.dashboard'), path: '/docente/dashboard', single: true },
        { key: 'mi-perfil-docente', icon: <IconIdcard />, title: t('teacher.profile'), path: '/docente/perfil', single: true },
        { key: 'clases-contenidos-docente', icon: <IconDesktop />, title: t('teacher.classesContent'), path: '/docente/clases-contenidos', pathPrefix: '/docente/clases-contenidos' },
        { key: 'actividades-docente', icon: <IconDesktop />, title: t('teacher.activities'), path: '/docente/actividades', pathPrefix: '/docente/actividades' },
        { key: 'evaluacion-docente', icon: <IconFile />, title: t('teacher.evaluation'), path: '/docente/evaluacion', pathPrefix: '/docente/evaluacion' },
        { key: 'seguimiento-docente', icon: <IconUserGroup />, title: t('teacher.tracking'), path: '/docente/seguimiento', pathPrefix: '/docente/seguimiento' },
        { key: 'finanzas-docente', icon: <IconApps />, title: t('teacher.finances'), path: '/docente/finanzas', pathPrefix: '/docente/finanzas' }
      ]
    },

    // Portal de Corporación
    {
      key: 'corporacion',
      icon: <IconUserGroup />,
      title: t('modules.corporation'),
      permission: 'corporation.portal.view',
      children: [
        {
          key: 'dashboard-corporacion',
          icon: <IconDashboard />,
          title: t('corporation.dashboard'),
          path: '/corporacion/dashboard',
          single: true,
          placeholder: true
        },
        { key: 'mi-organizacion', icon: <IconHome />, title: t('corporation.myOrganization'), path: '/corporacion/organizacion', single: true },
        { key: 'gestion-participantes', title: t('corporation.participantManagement'), path: '/corporacion/participantes', placeholder: true },
        { key: 'seguimiento-corporacion', title: t('corporation.tracking'), path: '/corporacion/seguimiento', placeholder: true },
        { key: 'reportes-corporacion', title: t('corporation.reports'), path: '/corporacion/reportes', placeholder: true },
        { key: 'comunicacion-corporacion', title: t('corporation.communication'), path: '/corporacion/comunicacion', placeholder: true }
      ]
    },

    // Base de Conocimiento (antes Hub Interno)
    {
      key: 'base-conocimiento',
      icon: <IconCompass />,
      title: t('modules.knowledge'),
      children: [
        { key: 'catalogo-programas-bc', title: t('knowledge.programCatalog'), path: '/base-conocimiento/catalogo', permission: 'knowledge.catalog.view' },
        { key: 'calendario-escolar-bc', title: t('knowledge.schoolCalendar'), path: '/base-conocimiento/calendario', permission: 'knowledge.calendar.view' },
        { key: 'organigrama-bc', title: t('knowledge.orgChart'), path: '/base-conocimiento/organigrama', permission: 'knowledge.catalog.view' },
      ]
    },

    // Académico
    {
      key: 'academico',
      icon: <IconDesktop />,
      title: t('modules.academic'),
      children: [
        {
          key: 'dashboard-academico',
          icon: <IconDashboard />,
          title: t('academic.dashboard'),
          path: '/academico/dashboard',
          single: true,
          permission: 'academic.dashboard.view'
        },
        {
          key: 'centros-estudios',
          icon: <IconHome />,
          title: t('academic.facilitiesSpaces'),
          permission: 'academic.centers.view',
          path: '/academico/centros',
          pathPrefix: '/academico/centros',
        },
        {
          key: 'diseno-instruccional',
          icon: <IconPen />,
          title: t('academic.instructionalDesign'),
          permission: 'academic.instructional_design.view',
          path: '/academico/diseno-instruccional',
          pathPrefix: '/academico/diseno-instruccional',
        },
        {
          key: 'programas-incorporados',
          icon: <IconFolder />,
          title: t('academic.incorporatedPrograms'),
          permission: 'academic.rvoe.view',
          path: '/academico/programas-incorporados',
          pathPrefix: '/academico/programas-incorporados',
        },
        {
          key: 'diseno-curricular',
          icon: <IconBook />,
          title: t('academic.curricularDesign'),
          permission: 'academic.plans.view',
          path: '/academico/diseno-curricular',
          pathPrefix: '/academico/diseno-curricular',
        },
        {
          key: 'capacitacion-corporativa',
          icon: <IconFile />,
          title: t('academic.corporateTraining'),
          permission: 'academic.projects.view',
          path: '/academico/capacitacion-corporativa',
          pathPrefix: '/academico/capacitacion-corporativa',
        },
        {
          key: 'planificacion-escolar',
          icon: <IconCalendar />,
          title: t('academic.schoolPlanning'),
          permission: 'academic.periods.view',
          path: '/academico/planificacion',
          pathPrefix: '/academico/planificacion',
        },
      ]
    },

    // Coordinación Carreras (2 niveles + tabs)
    {
      key: 'coordinacion-carreras',
      icon: <IconUserGroup />,
      title: t('modules.coordination'),
      children: [
        {
          key: 'mis-programas-coord',
          icon: <IconBook />,
          title: t('coordination.myPrograms'),
          permission: 'careers.programs.view',
          path: '/coordinacion-carreras/programas',
          pathPrefix: '/coordinacion-carreras/programas',
        },
        {
          key: 'contenidos-coord',
          icon: <IconFile />,
          title: t('coordination.contents'),
          permission: 'careers.contents.view',
          path: '/coordinacion-carreras/contenidos',
          pathPrefix: '/coordinacion-carreras/contenidos',
        },
        {
          key: 'gestion-docente',
          icon: <IconFolder />,
          title: t('coordination.teacherManagement'),
          permission: 'careers.teachers.view',
          path: '/coordinacion-carreras/docentes',
          pathPrefix: '/coordinacion-carreras/docentes',
        },
        {
          key: 'seguimiento-alumnos',
          icon: <IconUserGroup />,
          title: t('coordination.studentTracking'),
          permission: 'careers.students.view',
          path: '/coordinacion-carreras/seguimiento',
          pathPrefix: '/coordinacion-carreras/seguimiento',
        },
        {
          key: 'vinculacion',
          icon: <IconLaunch />,
          title: t('coordination.outreach'),
          permission: 'careers.outreach.view',
          path: '/coordinacion-carreras/vinculacion',
          pathPrefix: '/coordinacion-carreras/vinculacion',
        }
      ]
    },

    // Marketing (generación de demanda)
    {
      key: 'marketing',
      icon: <IconLaunch />,
      title: t('modules.marketing'),
      children: [
        {
          key: 'campanas',
          icon: <IconSend />,
          title: t('marketing.campaignsSection'),
          permission: 'marketing.campaigns.view',
          path: '/marketing/campanas',
          pathPrefix: '/marketing/campanas',
        },
        {
          key: 'redes-sociales',
          icon: <IconCompass />,
          title: t('marketing.socialMedia'),
          permission: 'marketing.campaigns.view',
          path: '/marketing/redes-sociales',
          pathPrefix: '/marketing/redes-sociales',
        },
        {
          key: 'contenido-comercial',
          icon: <IconFile />,
          title: t('marketing.commercialContent'),
          permission: 'marketing.campaigns.view',
          path: '/marketing/contenido',
          pathPrefix: '/marketing/contenido',
        },
        {
          key: 'descripcion-comercial',
          icon: <IconPen />,
          title: t('marketing.commercialDescription'),
          permission: 'marketing.campaigns.view',
          path: '/marketing/descripcion-comercial',
          pathPrefix: '/marketing/descripcion-comercial',
        },
        {
          key: 'analytics-marketing',
          icon: <IconExperiment />,
          title: t('marketing.analytics'),
          permission: 'marketing.campaigns.view',
          path: '/marketing/analytics',
          pathPrefix: '/marketing/analytics',
        }
      ]
    },

    // Comercial (catálogos y políticas comerciales)
    {
      key: 'comercial',
      icon: <IconTag />,
      title: t('modules.comercial'),
      children: [
        {
          key: 'lista-precios',
          icon: <IconApps />,
          title: t('comercial.pricing'),
          permission: 'comercial.pricing.view',
          path: '/comercial/precios',
          pathPrefix: '/comercial/precios',
        },
        {
          key: 'paquetes-comerciales',
          icon: <IconApps />,
          title: t('comercial.packages'),
          permission: 'comercial.packages.view',
          path: '/comercial/paquetes',
          pathPrefix: '/comercial/paquetes',
        },
        {
          key: 'convenios',
          icon: <IconFile />,
          title: t('comercial.agreements'),
          permission: 'comercial.agreements.view',
          path: '/comercial/convenios',
          pathPrefix: '/comercial/convenios',
        },
        {
          key: 'estimaciones',
          icon: <IconExperiment />,
          title: t('comercial.estimates'),
          permission: 'comercial.pricing.view',
          path: '/comercial/estimaciones',
          pathPrefix: '/comercial/estimaciones',
        }
      ]
    },

    // Ventas (cierre de tratos — CRM + Landing Pages)
    // Sprint 6: Comunicaciones + IA removed — smarthub-marketing retired
    {
      key: 'ventas',
      icon: <IconThunderbolt />,
      title: t('modules.sales'),
      children: [
        {
          key: 'crm-smartketing',
          icon: <IconApps />,
          title: t('ventas.crm'),
          permission: 'smarketing.contacts.view',
          path: '/ventas/crm',
          pathPrefix: '/ventas/crm',
        },
        {
          key: 'landing-pages-ventas',
          icon: <IconDesktop />,
          title: t('ventas.landingPages'),
          permission: 'smarketing.contacts.view',
          path: '/ventas/landing-pages',
          pathPrefix: '/ventas/landing-pages',
        },
      ]
    },

    // Servicios Escolares (2 niveles + tabs)
    {
      key: 'servicios-escolares',
      icon: <IconFolder />,
      title: t('modules.schoolServices'),
      children: [
        {
          key: 'lanzamientos-se',
          icon: <IconLaunch />,
          title: t('schoolServices.launches'),
          permission: 'school.launches.view',
          path: '/servicios-escolares/lanzamientos',
          pathPrefix: '/servicios-escolares/lanzamientos',
        },
        {
          key: 'inscripciones-se',
          icon: <IconFolder />,
          title: t('schoolServices.enrollment'),
          permission: 'school.enrollment.view',
          path: '/servicios-escolares/inscripciones',
          pathPrefix: '/servicios-escolares/inscripciones',
        },
        {
          key: 'expedientes-se',
          icon: <IconFile />,
          title: t('schoolServices.records'),
          permission: 'school.records.view',
          path: '/servicios-escolares/expedientes',
          pathPrefix: '/servicios-escolares/expedientes',
        },
        {
          key: 'control-escolar-se',
          icon: <IconFolder />,
          title: t('schoolServices.academicControl'),
          permission: 'school.control.view',
          path: '/servicios-escolares/control-escolar',
          pathPrefix: '/servicios-escolares/control-escolar',
        },
        {
          key: 'certificacion-se',
          icon: <IconFolder />,
          title: t('schoolServices.certification'),
          permission: 'school.certification.view',
          path: '/servicios-escolares/certificacion',
          pathPrefix: '/servicios-escolares/certificacion',
        }
      ]
    },

    // Contabilidad y Finanzas
    {
      key: 'contabilidad',
      icon: <IconApps />,
      title: t('modules.finance'),
      permission: 'finance.accounting.view',
      children: [
        { key: 'cobranza', title: t('finance.collections'), path: '/contabilidad/cobranza', pathPrefix: '/contabilidad/cobranza' },
        { key: 'facturacion', title: t('finance.billing'), path: '/contabilidad/facturacion', placeholder: true },
        { key: 'presupuesto', title: t('finance.budget'), path: '/contabilidad/presupuesto', placeholder: true },
        { key: 'revenue-corporativo', title: t('finance.corporateRevenue'), path: '/contabilidad/revenue', pathPrefix: '/contabilidad/revenue' },
        { key: 'costos-ia', title: t('finance.aiCosts'), path: '/contabilidad/costos-ia', pathPrefix: '/contabilidad/costos-ia' },
        { key: 'reportes-contabilidad', title: t('finance.reports'), path: '/contabilidad/reportes', pathPrefix: '/contabilidad/reportes' }
      ]
    },

    // Recursos Humanos (2 niveles + tabs)
    {
      key: 'recursos-humanos',
      icon: <IconUserGroup />,
      title: t('modules.hr'),
      permission: 'hr.management.view',
      children: [
        {
          key: 'personal-rrhh',
          icon: <IconUserGroup />,
          title: t('hr.directoryRecords'),
          path: '/rrhh/personal',
          pathPrefix: '/rrhh/personal',
        },
        {
          key: 'puestos-rrhh',
          icon: <IconApps />,
          title: t('hr.positions'),
          path: '/rrhh/puestos',
          pathPrefix: '/rrhh/puestos',
        },
        {
          key: 'altas-permisos-rrhh',
          icon: <IconFolder />,
          title: t('hr.hiringPermissions'),
          path: '/rrhh/altas-permisos',
          pathPrefix: '/rrhh/altas-permisos',
        },
        {
          key: 'nomina-rrhh',
          icon: <IconApps />,
          title: t('hr.payroll'),
          path: '/rrhh/nomina',
          pathPrefix: '/rrhh/nomina',
        },
        {
          key: 'admin-nomina-rrhh',
          icon: <IconApps />,
          title: t('hr.payrollAdmin'),
          path: '/rrhh/admin-nomina',
          pathPrefix: '/rrhh/admin-nomina',
        },
        {
          key: 'bienestar-rrhh',
          icon: <IconExperiment />,
          title: t('hr.wellbeingDevelopment'),
          path: '/rrhh/bienestar',
          pathPrefix: '/rrhh/bienestar',
        }
      ]
    },

    // Sistema
    {
      key: 'sistema',
      icon: <IconTool />,
      title: t('modules.system'),
      children: [
        {
          key: 'configuracion-general',
          icon: <IconSettings />,
          title: t('system.generalConfig'),
          permission: 'system.config.view',
          path: '/configuracion',
          pathPrefix: '/configuracion',
        },
        {
          key: 'permisos-sistema',
          icon: <IconLock />,
          title: t('system.permissions'),
          permission: 'system.config.view',
          path: '/sistema/permisos',
          pathPrefix: '/sistema/permisos',
        },
        {
          key: 'plantillas-sistema',
          icon: <IconFile />,
          title: t('system.templates'),
          permission: 'system.templates.view',
          path: '/sistema/plantillas',
          pathPrefix: '/sistema/plantillas',
        },
        {
          key: 'mantenimiento-menu',
          icon: <IconSettings />,
          title: t('system.maintenance'),
          permission: 'system.maintenance.view',
          path: '/mantenimiento',
          pathPrefix: '/mantenimiento',
        },
        {
          key: 'roadmap-menu',
          icon: <IconCompass />,
          title: t('system.roadmap'),
          permission: 'system.maintenance.view',
          path: '/sistema/roadmap',
          pathPrefix: '/sistema/roadmap',
        },
        {
          key: 'procedimientos-menu',
          icon: <IconMindMapping />,
          title: t('system.procedures'),
          permission: 'system.maintenance.view',
          path: '/sistema/procedimientos',
          pathPrefix: '/sistema/procedimientos',
        },
        {
          key: 'triggers-notificacion',
          icon: <IconNotification />,
          title: t('system.notificationTriggers'),
          permission: 'system.maintenance.view',
          path: '/sistema/triggers-notificacion',
          pathPrefix: '/sistema/triggers-notificacion',
        }
      ]
    }
  ];
  
  // Obtener información del módulo actual
  const getCurrentModule = () => {
    const path = location.pathname;

    // Académico
    if (path.includes('/academico/dashboard')) {
      return { name: 'Dashboard Académico', icon: <IconDashboard /> };
    } else if (path.includes('/academico/recursos')) {
      return { name: t('academic.facilitiesSpaces'), icon: <IconApps /> };
    } else if (path.includes('/academico/curricular')) {
      return { name: t('academic.curricularDesign'), icon: <IconBook /> };
    } else if (path.includes('/academico/proyectos')) {
      return { name: t('academic.corporateTraining'), icon: <IconFile /> };
    } else if (path.includes('/academico/calendario')) {
      return { name: t('academic.schoolPlanning'), icon: <IconCalendar /> };
    } else if (path.includes('/academico')) {
      return { name: t('modules.academic'), icon: <IconDesktop /> };
    }

    // Coordinación Carreras
    if (path.includes('/coordinacion-carreras')) {
      return { name: t('modules.coordination'), icon: <IconUser /> };
    }

    // Servicios Escolares
    if (path.includes('/servicios-escolares')) {
      return { name: t('modules.schoolServices'), icon: <IconUserGroup /> };
    }

    // Comercial
    if (path.includes('/comercial')) {
      return { name: t('modules.comercial'), icon: <IconTag /> };
    }

    // Ventas (CRM, Comunicaciones, IA)
    if (path.includes('/ventas')) {
      return { name: t('modules.sales'), icon: <IconThunderbolt /> };
    }

    // Alumno
    if (path.includes('/alumno/dashboard')) {
      return { name: t('student.dashboard'), icon: <IconDashboard /> };
    } else if (path.includes('/alumno')) {
      return { name: t('modules.student'), icon: <IconUser /> };
    }

    // Docente
    if (path.includes('/docente/dashboard')) {
      return { name: t('teacher.dashboard'), icon: <IconDashboard /> };
    } else if (path.includes('/docente/programas')) {
      return { name: t('coordination.myPrograms'), icon: <IconBook /> };
    } else if (path.includes('/docente')) {
      return { name: t('modules.teacher'), icon: <IconBook /> };
    }

    // Base de Conocimiento
    if (path.includes('/base-conocimiento')) {
      return { name: t('modules.knowledge'), icon: <IconCompass /> };
    }

    // Contabilidad y Finanzas
    if (path.includes('/contabilidad')) {
      return { name: t('modules.finance'), icon: <IconApps /> };
    }

    // Recursos Humanos
    if (path.includes('/rrhh')) {
      return { name: t('modules.hr'), icon: <IconUserGroup /> };
    }

    // Sistema
    if (path.includes('/configuracion/integraciones')) {
      return { name: t('system.integrations'), icon: <IconSettings /> };
    } else if (path.includes('/configuracion/calendarios-anuales')) {
      return { name: t('system.annualCalendars'), icon: <IconCalendar /> };
    } else if (path.includes('/sistema/permisos')) {
      return { name: t('system.permissions'), icon: <IconLock /> };
    } else if (path.includes('/configuracion')) {
      return { name: t('modules.system'), icon: <IconTool /> };
    }

    // Roadmap (antes de mantenimiento para que /sistema/roadmap no caiga en otra rama)
    if (path.includes('/sistema/roadmap')) {
      return { name: t('system.roadmap'), icon: <IconCompass /> };
    }

    // Mantenimiento
    if (path.includes('/mantenimiento/dashboard')) {
      return { name: t('system.dashboard'), icon: <IconDashboard /> };
    } else if (path.includes('/mantenimiento')) {
      return { name: t('system.maintenance'), icon: <IconSettings /> };
    }

    // Home
    if (path === '/') {
      return { name: t('modules.home'), icon: <IconHome /> };
    }

    return { name: 'SmartHub Center', icon: <IconHome /> };
  };
  
  // Toggle tema
  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    
    // Aplicar tema de Arco Design
    if (newTheme === 'dark') {
      document.body.setAttribute('arco-theme', 'dark');
    } else {
      document.body.removeAttribute('arco-theme');
    }
  };
  
  // Funciones de expansión/contracción
  const expandAllMenus = () => {
    const allKeys = menuItems
      .filter(item => item.children)
      .map(item => item.key);
    setOpenKeys(allKeys);
    setExpandAll(true);
  };

  const expandAllWithSubmenus = () => {
    const allKeys = [];
    menuItems.forEach(item => {
      if (item.children) {
        allKeys.push(item.key);
        item.children.forEach(subItem => {
          if (subItem.children) {
            allKeys.push(subItem.key);
          }
        });
      }
    });
    setOpenKeys(allKeys);
    setExpandAll(true);
  };

  const collapseAll = () => {
    setOpenKeys([]);
    setExpandAll(false);
  };
  
  // Manejar logout
  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };
  
  // Dropdown de usuario
  const userDropdown = (
    <Menu>
      <Menu.Item key='profile' onClick={() => Message.info(t('header.messages.inDevelopment'))}>
        <IconUser /> {t('header.myProfile')}
      </Menu.Item>
      <Menu.Item key='settings' onClick={() => Message.info(t('header.messages.inDevelopment'))}>
        <IconSettings /> {t('header.settings')}
      </Menu.Item>
      <div style={{ 
        margin: '4px 12px', 
        borderTop: '1px solid',
        borderColor: theme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
        opacity: 0.6
      }} />
      <Menu.Item key='logout' onClick={handleLogout}>
        <IconPoweroff /> {t('header.logout')}
      </Menu.Item>
    </Menu>
  );
  
  // Efecto para aplicar tema al cargar
  useEffect(() => {
    if (theme === 'dark') {
      document.body.setAttribute('arco-theme', 'dark');
    } else {
      document.body.removeAttribute('arco-theme');
    }
  }, []);

  // Efecto para forzar re-render del menú cuando cambia el usuario
  useEffect(() => {
    // Incrementar la key cada vez que cambie el usuario (login o logout)
    setMenuKey(prev => prev + 1);
  }, [user?.id]);

  // Version is now loaded via useQuery (queryKey: ['app', 'version'])

  // Efecto para expandir todo cuando hay búsqueda activa
  useEffect(() => {
    if (menuSearchText) {
      // Expandir todos los menús cuando hay búsqueda
      expandAllWithSubmenus();
    }
  }, [menuSearchText]);

  // Efecto para actualizar claves seleccionadas y abrir menús padres
  useEffect(() => {
    const path = location.pathname;

    // Encontrar la key del menú y sus padres
    const findKeyAndParents = (items, parentKeys = []) => {
      for (const item of items) {
        if (item.path === path) {
          return {
            key: item.key,
            parents: parentKeys
          };
        }
        if (item.children) {
          const result = findKeyAndParents(
            item.children,
            [...parentKeys, item.key]
          );
          if (result) return result;
        }
        // Prefix match para páginas con tabs (2 niveles + tabs)
        if (item.pathPrefix && path.startsWith(item.pathPrefix)) {
          return {
            key: item.key,
            parents: parentKeys
          };
        }
      }
      return null;
    };

    const result = findKeyAndParents(menuItems);
    if (result) {
      setSelectedKeys([result.key]);
      // Accordion: abrir solo los padres de la ruta actual, cerrando otros top-level
      if (result.parents.length > 0 && !expandAll) {
        const topLevelKeys = menuItems
          .filter(item => item.children)
          .map(item => item.key);
        const newTopLevel = result.parents.find(k => topLevelKeys.includes(k));

        setOpenKeys(prevKeys => {
          // Mantener submenús internos del mismo top-level, quitar otros top-level
          const filtered = prevKeys.filter(k => {
            if (topLevelKeys.includes(k)) return k === newTopLevel;
            return true;
          });
          const merged = [...new Set([...filtered, ...result.parents])];
          return merged;
        });
      }
    }
  }, [location, expandAll]);
  
  // Renderizar items del menú - Memoizado para re-renderizar cuando cambie el usuario o la búsqueda
  const filteredMenuItems = useMemo(() => {
    // Si no hay usuario, no mostrar nada
    if (!user) {
      return [];
    }

    // Función para verificar si el item debe mostrarse basado en permisos/roles
    const shouldShowItem = (item) => {
      // Si el item requiere un permiso específico, verificarlo
      if (item.permission) {
        // Superadmin tiene acceso a todo
        if (hasRole('superadmin')) {
          return true;
        }

        // Verificar si es un rol (ejemplo: 'superadmin', 'admin')
        if (item.permission === 'superadmin' || item.permission === 'admin') {
          const hasIt = hasRole(item.permission);
          if (!hasIt) {
            return false;
          }
        } else {
          // Es un permiso normal (ejemplo: 'academic.subjects.view')
          const hasIt = hasPermission(item.permission);
          if (!hasIt) {
            return false;
          }
        }
      }

      // Si tiene hijos, verificar si al menos uno debe mostrarse
      if (item.children) {
        const visibleChildren = item.children.filter(child => shouldShowItem(child));
        return visibleChildren.length > 0;
      }

      // Si no requiere permiso o lo tiene, mostrarlo
      return true;
    };

    // Función para verificar si el item coincide con la búsqueda
    const matchesSearch = (item) => {
      if (!menuSearchText) return true;

      const searchLower = menuSearchText.toLowerCase();
      const titleMatch = item.title?.toLowerCase().includes(searchLower);
      const descriptionMatch = item.description?.toLowerCase().includes(searchLower);

      // Si el item actual coincide, mostrarlo
      if (titleMatch || descriptionMatch) return true;

      // Si tiene hijos, verificar si alguno coincide
      if (item.children) {
        return item.children.some(child => matchesSearch(child));
      }

      return false;
    };

    // Filtrar recursivamente el menú
    const filterItems = (items) => {
      return items
        .filter(item => shouldShowItem(item) && matchesSearch(item))
        .map(item => {
          if (item.children) {
            return {
              ...item,
              children: filterItems(item.children)
            };
          }
          return item;
        });
    };

    const filtered = filterItems(menuItems);
    return filtered;
  }, [user, user?.permissions, hasPermission, menuSearchText, i18n.language]); // CRÍTICO: incluir menuSearchText e i18n.language

  const renderMenuItem = (item) => {
    if (item.children) {
      return (
        <SubMenu key={item.key} title={<span>{item.icon} {item.title}</span>}>
          {item.children.map(child => renderMenuItem(child))}
        </SubMenu>
      );
    }

    const titleStyle = item.placeholder ? { color: '#F53F3F' } : undefined;

    return (
      <Menu.Item key={item.key} onClick={() => item.path && navigate(item.path)}>
        {item.icon && <span style={titleStyle}>{item.icon}</span>}
        <span style={titleStyle}>{item.title}</span>
      </Menu.Item>
    );
  };
  
  const currentModule = getCurrentModule();
  
  return (
    <ConfigProvider
      locale={getArcoLocale(i18n.language)}
      theme={theme === 'dark' ? 'dark' : undefined}
    >
      <Layout className="app-layout">
        {/* Header */}
        <Header className="app-header">
          <div className="header-left">
            <div className="logo-container">
              <img src={logo} alt="SmartHub" className="logo" />
              <span className="logo-text">
                SmartHub
                {appVersion && (
                  <span style={{
                    fontSize: '14px',
                    color: '#165DFF',
                    marginLeft: '8px',
                    fontWeight: '600'
                  }}>
                    {appVersion}
                  </span>
                )}
              </span>
            </div>
            <div className="module-title">
              {currentModule.icon}
              <span>{currentModule.name}</span>
            </div>
          </div>
          
          <div className="header-right">
            <Space size="medium">
              <NotificationBell />
              
              <TaskBell />
              
              <LanguageSwitcher />

              <Button
                type="text"
                icon={theme === 'light' ? <IconMoon /> : <IconSun />}
                onClick={toggleTheme}
              />

              <Dropdown
                droplist={userDropdown} 
                position="br"
                trigger="click"
                unmountOnExit={true}
                getPopupContainer={() => document.body}
              >
                <div 
                  className="user-info-wrapper"
                  style={{ 
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    transition: 'background 0.2s'
                  }}
                >
                  <Avatar size={32} style={{ backgroundColor: '#165DFF' }}>
                    {user?.email?.charAt(0).toUpperCase() || 'U'}
                  </Avatar>
                  <div className="user-details" style={{ marginLeft: '10px' }}>
                    <div className="user-name">{user?.email || 'Usuario'}</div>
                    <div className="user-role">{user?.active_profile?.role?.name || 'Usuario'}</div>
                  </div>
                </div>
              </Dropdown>
            </Space>
          </div>
        </Header>
        
        <Layout>
          {/* Sidebar */}
          <Sider
            className="app-sidebar"
            width={280}
            collapsed={collapsed}
            onCollapse={setCollapsed}
            breakpoint="lg"
            collapsedWidth={0}
          >
            <div className="sidebar-controls">
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Input.Search
                  placeholder={t('header.searchMenu')}
                  size="small"
                  value={menuSearchText}
                  onChange={(value) => setMenuSearchText(value)}
                  allowClear
                />
                <Space size="small" style={{ width: '100%', justifyContent: 'center' }}>
                  <Tooltip content={t('header.expandMain')}>
                    <Button
                      type="text"
                      size="small"
                      icon={<IconMenuUnfold />}
                      onClick={expandAllMenus}
                    />
                  </Tooltip>
                  <Tooltip content={t('header.expandAll')}>
                    <Button
                      type="text"
                      size="small"
                      icon={<IconExpand />}
                      onClick={expandAllWithSubmenus}
                    />
                  </Tooltip>
                  <Tooltip content={t('header.collapseAll')}>
                    <Button
                      type="text"
                      size="small"
                      icon={<IconShrink />}
                      onClick={collapseAll}
                    />
                  </Tooltip>
                </Space>
              </Space>
            </div>
            
            <Menu
              key={menuKey}
              selectedKeys={selectedKeys}
              openKeys={openKeys}
              onClickMenuItem={(key) => setSelectedKeys([key])}
              onClickSubMenu={(key, currentOpenKeys) => {
                // Accordion: al abrir un menú de primer nivel, cerrar los demás de primer nivel
                const topLevelKeys = menuItems
                  .filter(item => item.children)
                  .map(item => item.key);
                const isTopLevel = topLevelKeys.includes(key);

                if (isTopLevel) {
                  const isOpening = currentOpenKeys.includes(key);
                  if (isOpening) {
                    // Mantener solo el key abierto + sus hijos internos (no otros top-level)
                    const filtered = currentOpenKeys.filter(k => !topLevelKeys.includes(k) || k === key);
                    setOpenKeys(filtered);
                  } else {
                    // Se está cerrando este top-level
                    setOpenKeys(currentOpenKeys);
                  }
                } else {
                  // Submenú interno (2do/3er nivel): comportamiento normal
                  setOpenKeys(currentOpenKeys);
                }
                setExpandAll(false);
              }}
              style={{ width: '100%' }}
            >
              {filteredMenuItems.map(item => renderMenuItem(item))}
            </Menu>
          </Sider>
          
          {/* Content */}
          <Layout>
            {/* Impersonation Banner */}
            {isImpersonating && impersonatedUser && (
              <div style={{
                background: '#FF7D00',
                color: '#fff',
                padding: '8px 24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                fontSize: '14px',
                fontWeight: 500,
                zIndex: 100,
              }}>
                <IconUser style={{ fontSize: 16 }} />
                <span>
                  {tSys('impersonation.banner.viewingAs')} <strong>{impersonatedUser.name || impersonatedUser.email}</strong>
                  {impersonatedUser.role && <span> ({impersonatedUser.role})</span>}
                  {' '}&mdash; {tSys('impersonation.banner.readOnly')}
                </span>
                <Button
                  size="small"
                  style={{
                    background: '#fff',
                    color: '#FF7D00',
                    border: 'none',
                    fontWeight: 600,
                    marginLeft: '8px',
                  }}
                  onClick={async () => {
                    await stopImpersonation();
                    navigate('/configuracion/usuarios');
                  }}
                >
                  {tSys('impersonation.banner.exit')}
                </Button>
              </div>
            )}
            <Content className="app-content">
              <div className="content-wrapper">
                {/* Breadcrumb removido - ahora cada página gestiona su propio breadcrumb */}
                <div className="content-body">
                  <Suspense fallback={
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                      <Spin size={40} />
                    </div>
                  }>
                    <Outlet />
                  </Suspense>
                </div>
              </div>
            </Content>
            
            {/* Footer */}
            <Footer className="app-footer">
              <div className="footer-content">
                {t('footer.copyright', { year: new Date().getFullYear() })}
              </div>
            </Footer>
          </Layout>
        </Layout>
      </Layout>
    </ConfigProvider>
  );
};

export default AppLayout;
